import React, { useEffect, useState, useMemo } from "react";
import { api, API } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Lock, Download, FileText, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  pitch: { fr: "Pitch & Présentation", en: "Pitch & Overview" },
  term_sheet: { fr: "Term Sheet & Note d'information", en: "Term Sheet & Memo" },
  shareholders: { fr: "Pacte d'actionnaires", en: "Shareholders' Agreement" },
  bylaws: { fr: "Statuts SPV", en: "SPV By-laws" },
  technical: { fr: "Rapports techniques GPU", en: "GPU Technical Reports" },
  compliance: { fr: "Conformité AMF / DICI", en: "AMF / KIID Compliance" },
  other: { fr: "Autres", en: "Other" },
};

const ACCESS_LABEL = {
  public: { fr: "Public", en: "Public", color: "bg-slate-100 text-slate-700 border-slate-200" },
  registered: { fr: "Compte requis", en: "Account required", color: "bg-amber-50 text-amber-800 border-amber-200" },
  qualified: { fr: "Qualifié + NDA", en: "Qualified + NDA", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function DataRoom() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [nda, setNda] = useState(null);
  const [showNda, setShowNda] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, n] = await Promise.all([
        api.get("/data-room/documents"),
        api.get("/data-room/nda", { params: { lang } }),
      ]);
      setDocs(d.data);
      setNda(n.data);
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur de chargement de la data room" : "Failed to load data room");
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [lang]);

  const grouped = useMemo(() => {
    const m = {};
    docs.forEach((d) => {
      m[d.category] = m[d.category] || [];
      m[d.category].push(d);
    });
    return m;
  }, [docs]);

  const acceptNda = async () => {
    if (!agreed) return;
    setBusy(true);
    try {
      await api.post("/data-room/nda/accept");
      toast.success(lang === "fr" ? "NDA accepté — accès aux documents confidentiels" : "NDA accepted — confidential access unlocked");
      setShowNda(false);
      setAgreed(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setBusy(false);
    }
  };

  const downloadDoc = async (d) => {
    if (!d.accessible) {
      // Targeted CTA depending on the missing condition
      if (d.access_level === "registered" && !user) {
        toast.info(lang === "fr" ? "Connectez-vous pour accéder à ce document." : "Sign in to access this document.");
        return;
      }
      if (d.access_level === "qualified") {
        if (!user) {
          toast.info(lang === "fr" ? "Connectez-vous pour accéder." : "Sign in to access.");
          return;
        }
        if (user?.kyc_status !== "qualified") {
          toast.info(lang === "fr" ? "Qualification KYC requise." : "KYC qualification required.");
          return;
        }
        if (!nda?.accepted) {
          setShowNda(true);
          return;
        }
      }
      return;
    }
    // Trigger download via fetch with auth header
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API}/data-room/documents/${d.doc_id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(lang === "fr" ? "Téléchargement impossible" : "Download failed");
    }
  };

  const ndaAccepted = !!nda?.accepted;
  const isQualified = user?.kyc_status === "qualified";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="label-divider mb-3">{lang === "fr" ? "Data Room" : "Data Room"}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900" data-testid="dataroom-title">
        {lang === "fr" ? "Documents & Diligence" : "Documents & Due Diligence"}
      </h1>
      <p className="text-sm text-slate-500 mt-2 max-w-3xl">
        {lang === "fr"
          ? "Trois niveaux d'accès : Public · Compte requis · Investisseur qualifié + NDA. Tous les documents sont versionnés et signés numériquement par InvestGPU."
          : "Three access tiers: Public · Account required · Qualified investor + NDA. All documents are versioned and digitally signed by InvestGPU."}
      </p>

      {/* Access summary card */}
      <div className="mt-8 grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden" data-testid="dataroom-access-summary">
        <AccessTile
          label={lang === "fr" ? "Public" : "Public"}
          status="ok"
          desc={lang === "fr" ? "Accessible à tous." : "Open to all."}
        />
        <AccessTile
          label={lang === "fr" ? "Compte requis" : "Account required"}
          status={user ? "ok" : "ko"}
          desc={user ? (lang === "fr" ? "Vous êtes connecté." : "You are signed in.") : (lang === "fr" ? "Connectez-vous." : "Sign in.")}
          cta={user ? null : { to: "/login", label: lang === "fr" ? "Se connecter" : "Sign in" }}
        />
        <AccessTile
          label={lang === "fr" ? "Qualifié + NDA" : "Qualified + NDA"}
          status={isQualified && ndaAccepted ? "ok" : "ko"}
          desc={
            !user ? (lang === "fr" ? "Connectez-vous." : "Sign in.")
            : !isQualified ? (lang === "fr" ? "Qualification KYC requise." : "KYC qualification required.")
            : !ndaAccepted ? (lang === "fr" ? "NDA à signer (clic-accept)." : "NDA pending (click-accept).")
            : (lang === "fr" ? `Acceptée le ${(nda?.accepted_at || "").slice(0, 10)}` : `Accepted ${(nda?.accepted_at || "").slice(0, 10)}`)
          }
          cta={
            !user ? { to: "/login", label: lang === "fr" ? "Se connecter" : "Sign in" }
            : !isQualified ? { to: "/qualification", label: lang === "fr" ? "Compléter KYC" : "Complete KYC" }
            : !ndaAccepted ? { onClick: () => setShowNda(true), label: lang === "fr" ? "Lire & signer NDA" : "Read & sign NDA", testid: "dataroom-open-nda" }
            : null
          }
        />
      </div>

      {/* Documents by category */}
      <div className="mt-10 space-y-10" data-testid="dataroom-list">
        {Object.keys(CATEGORY_LABEL).map((cat) => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          return (
            <section key={cat}>
              <div className="flex items-end justify-between border-b border-slate-200 pb-2 mb-4">
                <h2 className="font-display text-base sm:text-lg font-bold text-slate-900">
                  {CATEGORY_LABEL[cat][lang] || cat}
                </h2>
                <span className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-mono">{items.length} doc{items.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {items.map((d) => (
                  <DocCard
                    key={d.doc_id}
                    doc={d}
                    onDownload={() => downloadDoc(d)}
                    lang={lang}
                  />
                ))}
              </div>
            </section>
          );
        })}
        {docs.length === 0 && (
          <div className="text-slate-500 text-sm py-12 text-center" data-testid="dataroom-empty">
            {lang === "fr" ? "Aucun document disponible pour le moment." : "No documents available yet."}
          </div>
        )}
      </div>

      {/* NDA modal */}
      <Dialog open={showNda} onOpenChange={(v) => { if (!busy) setShowNda(v); }}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="nda-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{lang === "fr" ? "Accord de confidentialité" : "Non-Disclosure Agreement"}</DialogTitle>
            <DialogDescription className="text-xs uppercase tracking-[0.15em] text-slate-500">
              {lang === "fr" ? `Version ${nda?.version || "—"} · clic-accept horodaté` : `Version ${nda?.version || "—"} · timestamped click-accept`}
            </DialogDescription>
          </DialogHeader>
          <div className="border border-slate-200 rounded-sm bg-slate-50 p-4 max-h-72 overflow-auto text-sm text-slate-700 whitespace-pre-line font-sans" data-testid="nda-text">
            {nda?.text || ""}
          </div>
          <label className="flex items-start gap-3 mt-4 cursor-pointer">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5"
              data-testid="nda-agree-checkbox"
            />
            <span className="text-sm text-slate-700">
              {lang === "fr"
                ? "J'ai lu et j'accepte sans réserve l'accord de confidentialité ci-dessus. Mon adresse IP et la date d'acceptation seront enregistrées."
                : "I have read and unconditionally accept the NDA above. My IP address and acceptance timestamp will be recorded."}
            </span>
          </label>
          <DialogFooter className="mt-6">
            <Button variant="outline" className="rounded-sm" onClick={() => setShowNda(false)} disabled={busy} data-testid="nda-cancel">
              {lang === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button
              className="rounded-sm bg-emerald-700 hover:bg-emerald-800"
              onClick={acceptNda}
              disabled={!agreed || busy}
              data-testid="nda-confirm"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {busy ? "…" : (lang === "fr" ? "Accepter et déverrouiller" : "Accept and unlock")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccessTile({ label, status, desc, cta }) {
  const ok = status === "ok";
  return (
    <div className="bg-white p-5">
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <Lock className="h-4 w-4 text-slate-400" />}
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-600 font-bold">{label}</div>
      </div>
      <div className="mt-2 text-sm text-slate-700">{desc}</div>
      {cta && (
        cta.to ? (
          <Button asChild size="sm" variant="outline" className="rounded-sm mt-3 h-8 text-xs">
            <Link to={cta.to}>{cta.label}</Link>
          </Button>
        ) : (
          <Button size="sm" className="rounded-sm mt-3 h-8 text-xs bg-slate-900 hover:bg-slate-800" onClick={cta.onClick} data-testid={cta.testid}>
            {cta.label}
          </Button>
        )
      )}
    </div>
  );
}

function DocCard({ doc, onDownload, lang }) {
  const al = ACCESS_LABEL[doc.access_level] || ACCESS_LABEL.public;
  const locked = !doc.accessible;
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 flex gap-4" data-testid={`doc-card-${doc.doc_id}`}>
      <div className={`shrink-0 w-10 h-10 rounded-sm border flex items-center justify-center ${locked ? "bg-slate-100 border-slate-200 text-slate-400" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
        {locked ? <Lock className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase tracking-[0.12em] font-bold border rounded-sm px-1.5 py-0.5 ${al.color}`}>{al[lang]}</span>
          <span className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-mono">v{doc.version} · {fmtSize(doc.size_bytes)}</span>
        </div>
        <div className="font-medium text-slate-900 mt-1.5 truncate" title={doc.title}>{doc.title}</div>
        {doc.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{doc.description}</div>}
        <div className="mt-3">
          <Button
            size="sm"
            variant={locked ? "outline" : "default"}
            className={`rounded-sm h-8 text-xs ${locked ? "" : "bg-slate-900 hover:bg-slate-800"}`}
            onClick={onDownload}
            data-testid={`doc-download-${doc.doc_id}`}
          >
            {locked ? (
              <><AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> {lang === "fr" ? "Accès verrouillé" : "Locked"}</>
            ) : (
              <><Download className="h-3.5 w-3.5 mr-1.5" /> {lang === "fr" ? "Télécharger" : "Download"}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
